import { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, CalendarDays, CalendarPlus, FileHeart, LogOut, Send } from "lucide-react";
import {
  createPatientPortalAppointment,
  loadPatientPortalData,
  loadPatientPortalReservationOptions,
  notifyAppointmentEmail,
  patientPortalLogin,
} from "./data.js";
import { dateLabel, friendlyError, jakartaDateKey } from "./lib/format.js";

const SESSION_KEY = "xv_patient_portal_session";

function SafeText({ children }) {
  if (!children) return null;
  return <p style={{ margin: "6px 0 0", color: "#4b5563", lineHeight: 1.55 }}>{children}</p>;
}

function PatientNoteItem({ label, children }) {
  if (!children) return null;
  return (
    <div className="patient-note-item">
      <span>{label}</span>
      <p>{children}</p>
    </div>
  );
}

function addDays(dateStr, days) {
  const date = new Date(`${dateStr}T00:00:00+07:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function fmtTime(value) {
  if (!value) return "-";
  return String(value).slice(0, 5);
}

function minutesFromTime(value) {
  const [hours, minutes] = String(value || "00:00").slice(0, 5).split(":").map(Number);
  return (hours * 60) + (minutes || 0);
}

function timeFromMinutes(value) {
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function jakartaMinutesNow(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Jakarta",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return (Number(values.hour || 0) * 60) + Number(values.minute || 0);
}

function dayOfWeek(dateStr) {
  if (!dateStr) return -1;
  const [year, month, day] = String(dateStr).split("-").map(Number);
  if (!year || !month || !day) return -1;
  return new Date(Date.UTC(year, month - 1, day, 12)).getUTCDay();
}

const DAYS_ID = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];

function isReservationProvider(row) {
  return !/asisten|assistant/i.test(row?.competencies || "");
}

function LoginCard({ onLoggedIn }) {
  const [form, setForm] = useState({ phone: "", client_code: "", token: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function set(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function submit(event) {
    event.preventDefault();
    setError("");
    if (!form.phone.trim() || !form.client_code.trim() || !form.token.trim()) {
      setError("Nomor telepon, kode klien, dan token wajib diisi.");
      return;
    }
    setBusy(true);
    try {
      const result = await patientPortalLogin(form);
      window.localStorage.setItem(SESSION_KEY, result.session_token);
      onLoggedIn(result.session_token);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="patient-portal-shell">
      <section className="patient-login-card">
        <div className="patient-brand">XV</div>
        <span className="patient-eyebrow">Portal Pasien</span>
        <h1>Masuk untuk melihat progress dan reservasi</h1>
        <p>Gunakan nomor telepon, kode klien, dan token yang diberikan saat registrasi.</p>
        {error ? <div className="patient-alert">{error}</div> : null}
        <form onSubmit={submit} className="patient-form">
          <label>
            Nomor telepon
            <input value={form.phone} onChange={(event) => set("phone", event.target.value)} placeholder="0812..." inputMode="tel" />
          </label>
          <label>
            Kode klien / ID klien
            <input value={form.client_code} onChange={(event) => set("client_code", event.target.value)} placeholder="KL-..." />
          </label>
          <label>
            Token akses
            <input value={form.token} onChange={(event) => set("token", event.target.value.toUpperCase())} placeholder="8 digit token" />
          </label>
          <button disabled={busy}>{busy ? "Memeriksa..." : "Masuk Portal Pasien"}</button>
        </form>
        <small>Data hanya dapat dibuka jika token masih aktif dan cocok dengan nomor telepon pasien.</small>
      </section>
    </main>
  );
}

function VisitCard({ visit }) {
  const note = visit.medical_note || visit.estetik_note || visit.dental_note;
  const procedure = visit.estetik_note?.procedure_plan || visit.dental_note?.procedure_plan;
  const diagnosis = note?.syndrome_status || note?.diagnosis;
  return (
    <article className="patient-card">
      <div className="patient-card-head">
        <div>
          <span>{dateLabel(visit.visit_date)} - {visit.division || "Klinik"}</span>
          <h3>{visit.visit_number}</h3>
        </div>
      </div>
      <div className="patient-meta">
        <span><CalendarDays size={15} /> {visit.doctor_name || "Nakes belum ditentukan"}</span>
        <span><Activity size={15} /> Pembayaran: {visit.payment_status || "-"}</span>
      </div>
      {note ? (
        <div className="patient-note">
          <strong>Ringkasan untuk Pasien</strong>
          <PatientNoteItem label="Keluhan yang dicatat">{note.complaint}</PatientNoteItem>
          <PatientNoteItem label="Penilaian nakes">{diagnosis}</PatientNoteItem>
          <PatientNoteItem label="Perkembangan terakhir">{note.improvement_status}</PatientNoteItem>
          <PatientNoteItem label="Catatan tindakan">{note.action_notes}</PatientNoteItem>
          <PatientNoteItem label="Instruksi setelah tindakan">{note.postcare_instructions}</PatientNoteItem>
          <PatientNoteItem label="Anjuran perawatan rumah">{note.homecare_prescription}</PatientNoteItem>
          <PatientNoteItem label="Rencana tindak lanjut">{note.followup_plan}</PatientNoteItem>
          {note.followup_date ? <PatientNoteItem label="Kontrol berikutnya">{dateLabel(note.followup_date)}</PatientNoteItem> : null}
          {procedure?.sessions ? <PatientNoteItem label="Rencana sesi">{procedure.sessions}</PatientNoteItem> : null}
        </div>
      ) : (
        <div className="patient-note patient-note-muted">
          Ringkasan medis belum tersedia atau masih menunggu finalisasi nakes.
        </div>
      )}
    </article>
  );
}

function AppointmentCard({ appointment, staffMap }) {
  const nakesName = staffMap[appointment.nakes_id]?.name || "Menunggu penjadwalan klinik";
  return (
    <article className="patient-appointment-card">
      <div>
        <strong>{dateLabel(appointment.appointment_date)} - {fmtTime(appointment.appointment_time)}</strong>
        <span>{appointment.division || "Klinik"} - {nakesName}</span>
      </div>
      <em>{appointment.status || "SCHEDULED"}</em>
    </article>
  );
}

function ReservationPanel({ sessionToken }) {
  const today = jakartaDateKey();
  const [options, setOptions] = useState({ divisions: [], staff: [], schedules: [], busy_slots: [], slot_minutes: {}, appointments: [] });
  const [form, setForm] = useState({
    division: "WELLNESS",
    nakes_id: "",
    appointment_date: today,
    appointment_time: "10:00",
    notes: "",
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadOptions = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await loadPatientPortalReservationOptions(sessionToken);
      setOptions(result || { divisions: [], staff: [], schedules: [], busy_slots: [], slot_minutes: {}, appointments: [] });
      const divisions = result?.divisions || [];
      const firstDivision = divisions[0]?.value || "WELLNESS";
      setForm((current) => ({
        ...current,
        division: divisions.some((row) => row.value === current.division) ? current.division : firstDivision,
        nakes_id: "",
      }));
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const staffMap = useMemo(() => Object.fromEntries((options.staff || []).map((row) => [row.id, row])), [options.staff]);
  const schedules = useMemo(() => options.schedules || [], [options.schedules]);
  const busySlots = useMemo(() => options.busy_slots || [], [options.busy_slots]);
  const currentSlotMinutes = Number(options.slot_minutes?.[form.division] || 60);
  const filteredStaff = useMemo(() => (
    (options.staff || []).filter((row) => {
      if (!isReservationProvider(row)) return false;
      const staffMatches = !row.division || row.division === "ALL" || row.division === form.division;
      const hasSchedule = schedules.some((schedule) =>
        schedule.nakes_id === row.id
        && (!schedule.division || schedule.division === "ALL" || schedule.division === form.division)
      );
      return staffMatches && hasSchedule;
    })
  ), [options.staff, schedules, form.division]);

  const selectedSchedules = useMemo(() => (
    schedules.filter((schedule) =>
      schedule.nakes_id === form.nakes_id
      && (!schedule.division || schedule.division === "ALL" || schedule.division === form.division)
    )
  ), [schedules, form.nakes_id, form.division]);

  const scheduleSummary = useMemo(() => {
    if (!selectedSchedules.length) return "";
    return selectedSchedules
      .map((schedule) => `${DAYS_ID[Number(schedule.day_of_week)] || schedule.day_of_week} ${fmtTime(schedule.start_time)}-${fmtTime(schedule.end_time)}`)
      .join(", ");
  }, [selectedSchedules]);

  const availableSlots = useMemo(() => {
    if (!form.nakes_id || !form.appointment_date) return [];
    if (form.appointment_date < today) return [];
    const currentDow = dayOfWeek(form.appointment_date);
    const daySchedules = selectedSchedules.filter((schedule) => Number(schedule.day_of_week) === currentDow);
    const currentMinute = form.appointment_date === today ? jakartaMinutesNow() : -1;
    const busy = busySlots
        .filter((slot) => slot.nakes_id === form.nakes_id && slot.appointment_date === form.appointment_date)
        .map((slot) => ({
          start: minutesFromTime(slot.appointment_time),
          end: minutesFromTime(slot.appointment_time) + Number(slot.duration_minutes || 60),
        }));
    const slots = [];
    for (const schedule of daySchedules) {
      const start = minutesFromTime(schedule.start_time);
      const end = minutesFromTime(schedule.end_time);
      for (let minute = start; minute + currentSlotMinutes <= end; minute += currentSlotMinutes) {
        const value = timeFromMinutes(minute);
        const candidateEnd = minute + currentSlotMinutes;
        const overlaps = busy.some((slot) => minute < slot.end && candidateEnd > slot.start);
        if (minute > currentMinute && !overlaps) slots.push(value);
      }
    }
    return [...new Set(slots)].sort();
  }, [busySlots, currentSlotMinutes, form.appointment_date, form.nakes_id, selectedSchedules, today]);

  useEffect(() => {
    setForm((current) => {
      if (!current.nakes_id) return current;
      if (availableSlots.includes(current.appointment_time)) return current;
      return { ...current, appointment_time: availableSlots[0] || "" };
    });
  }, [availableSlots]);

  function update(key, value) {
    setNotice("");
    setError("");
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (key === "division") {
        next.nakes_id = "";
        next.appointment_time = "";
      }
      if (key === "nakes_id" || key === "appointment_date") {
        next.appointment_time = "";
      }
      return next;
    });
  }

  async function submit(event) {
    event.preventDefault();
    setError("");
    setNotice("");
    if (!form.division) return setError("Pilih divisi reservasi.");
    if (!form.nakes_id) return setError("Pilih nakes/dokter terlebih dahulu.");
    if (!form.appointment_date) return setError("Pilih tanggal reservasi.");
    if (!form.appointment_time) return setError("Pilih jam praktik yang tersedia.");
    setBusy(true);
    try {
      const result = await createPatientPortalAppointment(sessionToken, form);
      let emailStatus = "";
      try {
        const emailResult = await notifyAppointmentEmail(result?.appointment?.id);
        if (emailResult?.status === "sent") {
          emailStatus = " Dokter sudah diberi notifikasi email.";
        }
      } catch {
        // Notifikasi email tidak boleh membatalkan reservasi yang sudah berhasil dibuat.
      }
      setNotice(`${result?.message || "Reservasi berhasil dibuat."}${emailStatus}`);
      setForm((current) => ({ ...current, notes: "" }));
      await loadOptions();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="patient-section">
      <div className="patient-section-head">
        <div>
          <h2>Reservasi Kunjungan</h2>
          <p>Pilih jadwal yang diinginkan. Tim klinik akan mengonfirmasi reservasi Anda.</p>
        </div>
        <CalendarPlus size={22} />
      </div>

      <form className="patient-reservation" onSubmit={submit}>
        {error ? <div className="patient-alert">{error}</div> : null}
        {notice ? <div className="patient-success">{notice}</div> : null}
        <label>
          Divisi
          <select value={form.division} onChange={(event) => update("division", event.target.value)} disabled={loading || busy}>
            {(options.divisions || []).map((row) => (
              <option key={row.value} value={row.value}>{row.label}</option>
            ))}
          </select>
        </label>
        <label>
          Nakes / dokter
          <select value={form.nakes_id} onChange={(event) => update("nakes_id", event.target.value)} disabled={loading || busy}>
            <option value="">Pilih nakes/dokter</option>
            {filteredStaff.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
          {form.nakes_id && scheduleSummary ? <small>Jadwal: {scheduleSummary}</small> : null}
        </label>
        <label>
          Tanggal
          <input
            type="date"
            min={today}
            max={addDays(today, 30)}
            value={form.appointment_date}
            onChange={(event) => update("appointment_date", event.target.value)}
            disabled={loading || busy}
          />
        </label>
        <label>
          Jam
          <select
            value={form.appointment_time}
            onChange={(event) => update("appointment_time", event.target.value)}
            disabled={loading || busy || !form.nakes_id || !form.appointment_date || !availableSlots.length}
          >
            {availableSlots.length ? (
              availableSlots.map((slot) => <option key={slot} value={slot}>{slot}</option>)
            ) : (
              <option value="">Tidak ada slot</option>
            )}
          </select>
          {form.nakes_id && form.appointment_date && !availableSlots.length ? (
            <small>Dokter tidak praktik atau slot sudah penuh pada tanggal ini.</small>
          ) : <small>Durasi slot: {currentSlotMinutes} menit.</small>}
        </label>
        <label className="patient-reservation-wide">
          Catatan untuk klinik
          <textarea
            rows="3"
            value={form.notes}
            onChange={(event) => update("notes", event.target.value)}
            placeholder="Contoh: ingin scaling, kontrol nyeri gigi, atau preferensi jam..."
            disabled={loading || busy}
          />
        </label>
        <button disabled={loading || busy}>
          <Send size={17} /> {busy ? "Mengirim..." : "Kirim Reservasi"}
        </button>
      </form>

      <h3 className="patient-subtitle">Reservasi Saya</h3>
      {loading ? (
        <div className="patient-empty">Memuat reservasi...</div>
      ) : options.appointments?.length ? (
        <div className="patient-appointment-list">
          {options.appointments.map((appointment) => (
            <AppointmentCard key={appointment.id} appointment={appointment} staffMap={staffMap} />
          ))}
        </div>
      ) : (
        <div className="patient-empty">Belum ada reservasi aktif.</div>
      )}
    </section>
  );
}

function PortalDashboard({ sessionToken, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("progress");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const result = await loadPatientPortalData(sessionToken);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError(friendlyError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionToken]);

  const latestVisit = useMemo(() => data?.visits?.[0] || null, [data]);

  if (loading) return <main className="patient-portal-shell"><div className="patient-loading">Memuat data pasien...</div></main>;

  if (error) {
    return (
      <main className="patient-portal-shell">
        <section className="patient-login-card">
          <div className="patient-alert">{error}</div>
          <button onClick={onLogout} className="patient-secondary">Masuk ulang</button>
        </section>
      </main>
    );
  }

  return (
    <main className="patient-dashboard">
      <header className="patient-header">
        <div>
          <span className="patient-eyebrow">XV Patient Portal</span>
          <h1>Halo, {data.client?.name || "Pasien"}</h1>
          <p>{data.client?.client_code} - {data.client?.phone}</p>
        </div>
        <button onClick={onLogout}><LogOut size={17} /> Keluar</button>
      </header>
      <section className="patient-summary">
        <article>
          <FileHeart size={22} />
          <span>Total kunjungan</span>
          <strong>{data.visits?.length || 0}</strong>
        </article>
        <article>
          <Activity size={22} />
          <span>Pembayaran terakhir</span>
          <strong>{latestVisit?.payment_status || "-"}</strong>
        </article>
      </section>
      <nav className="patient-tabs">
        <button className={tab === "progress" ? "active" : ""} onClick={() => setTab("progress")}>Progress</button>
        <button className={tab === "reservation" ? "active" : ""} onClick={() => setTab("reservation")}>Reservasi</button>
      </nav>
      {tab === "progress" ? (
        <section className="patient-section">
          <h2>Progress Kunjungan</h2>
          {data.visits?.length ? data.visits.map((visit) => <VisitCard key={visit.id} visit={visit} />) : (
            <div className="patient-empty">Belum ada kunjungan yang dapat ditampilkan.</div>
          )}
        </section>
      ) : (
        <ReservationPanel sessionToken={sessionToken} />
      )}
    </main>
  );
}

export function PatientPortalPage() {
  const [sessionToken, setSessionToken] = useState(() => window.localStorage.getItem(SESSION_KEY) || "");

  function logout() {
    window.localStorage.removeItem(SESSION_KEY);
    setSessionToken("");
  }

  return (
    <>
      {sessionToken ? (
        <PortalDashboard sessionToken={sessionToken} onLogout={logout} />
      ) : (
        <LoginCard onLoggedIn={setSessionToken} />
      )}
      <style>{`
        .patient-portal-shell { min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 24px; background: #eefbf4; color: #143326; font-family: Inter, system-ui, sans-serif; }
        .patient-login-card { width: min(460px, 100%); background: #fffdf8; border: 1px solid #c9f2d8; border-radius: 18px; box-shadow: 0 22px 80px rgba(22, 101, 52, .15); padding: 32px; }
        .patient-brand { width: 48px; height: 48px; display: grid; place-items: center; border-radius: 14px; background: #f7cc6b; font-family: Georgia, serif; font-weight: 900; margin-bottom: 18px; }
        .patient-eyebrow { color: #16a34a; display: block; font-size: 12px; font-weight: 900; letter-spacing: .18em; text-transform: uppercase; }
        .patient-login-card h1 { margin: 8px 0; font-family: Georgia, serif; font-size: 32px; line-height: 1.05; }
        .patient-login-card p { color: #5f7168; line-height: 1.5; margin: 0 0 20px; }
        .patient-form { display: grid; gap: 14px; }
        .patient-form label { display: grid; gap: 7px; font-size: 13px; font-weight: 800; }
        .patient-form input { border: 1px solid #baf0cc; border-radius: 12px; font: inherit; font-size: 16px; padding: 13px 14px; }
        .patient-form button, .patient-header button, .patient-secondary { align-items: center; background: #16a34a; border: 0; border-radius: 12px; color: white; cursor: pointer; display: inline-flex; font: inherit; font-weight: 900; gap: 8px; justify-content: center; padding: 14px 16px; }
        .patient-form button:disabled { cursor: wait; opacity: .7; }
        .patient-login-card small { color: #7b8b83; display: block; font-size: 12px; line-height: 1.45; margin-top: 16px; }
        .patient-alert { background: #fee2e2; border: 1px solid #fecaca; border-radius: 12px; color: #991b1b; font-weight: 700; margin-bottom: 16px; padding: 12px 14px; }
        .patient-loading { color: #5f7168; font-weight: 800; }
        .patient-dashboard { min-height: 100vh; background: #eefbf4; color: #143326; font-family: Inter, system-ui, sans-serif; padding: 32px clamp(18px, 5vw, 72px); }
        .patient-header { align-items: center; display: flex; gap: 18px; justify-content: space-between; margin: 0 auto 24px; max-width: 1080px; }
        .patient-header h1 { font-family: Georgia, serif; font-size: clamp(30px, 5vw, 48px); margin: 7px 0; }
        .patient-header p { color: #5f7168; margin: 0; }
        .patient-summary { display: grid; gap: 14px; grid-template-columns: repeat(2, minmax(0, 1fr)); margin: 0 auto 24px; max-width: 1080px; }
        .patient-summary article { background: #fffdf8; border: 1px solid #c9f2d8; border-radius: 14px; padding: 18px; }
        .patient-summary span { color: #5f7168; display: block; font-size: 13px; font-weight: 800; margin-top: 10px; }
        .patient-summary strong { display: block; font-size: 24px; margin-top: 4px; }
        .patient-section { margin: 0 auto; max-width: 1080px; }
        .patient-section-head { align-items: center; display: flex; gap: 12px; justify-content: space-between; margin-bottom: 14px; }
        .patient-section-head h2 { margin: 0 0 4px; }
        .patient-section-head p { color: #5f7168; margin: 0; }
        .patient-section h2 { font-size: 22px; margin: 0 0 14px; }
        .patient-tabs { display: flex; gap: 8px; margin: 0 auto 22px; max-width: 1080px; }
        .patient-tabs button { background: #fffdf8; border: 1px solid #c9f2d8; border-radius: 999px; color: #166534; cursor: pointer; font: inherit; font-weight: 900; padding: 10px 18px; }
        .patient-tabs button.active { background: #16a34a; color: #fff; }
        .patient-card { background: #fffdf8; border: 1px solid #c9f2d8; border-radius: 14px; margin-bottom: 14px; padding: 20px; }
        .patient-card-head { align-items: flex-start; display: flex; gap: 12px; justify-content: space-between; }
        .patient-card-head span { color: #5f7168; font-size: 13px; font-weight: 700; }
        .patient-card-head h3 { margin: 5px 0 0; }
        .patient-meta { color: #5f7168; display: flex; flex-wrap: wrap; gap: 12px; margin-top: 14px; }
        .patient-meta span { align-items: center; display: inline-flex; gap: 6px; }
        .patient-note { background: #f8fbf5; border-radius: 12px; margin-top: 14px; padding: 14px; }
        .patient-note > strong { display: block; margin-bottom: 10px; }
        .patient-note-item { border-top: 1px solid #e7f3e7; padding: 10px 0; }
        .patient-note-item:first-of-type { border-top: 0; padding-top: 0; }
        .patient-note-item:last-child { padding-bottom: 0; }
        .patient-note-item span { color: #15803d; display: block; font-size: 12px; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; }
        .patient-note-item p { color: #4b5563; line-height: 1.55; margin: 4px 0 0; white-space: pre-line; }
        .patient-note-muted { color: #7b8b83; font-weight: 700; }
        .patient-empty { background: #fffdf8; border: 1px solid #c9f2d8; border-radius: 14px; color: #7b8b83; padding: 32px; text-align: center; }
        .patient-success { background: #dcfce7; border: 1px solid #bbf7d0; border-radius: 12px; color: #166534; font-weight: 800; margin-bottom: 12px; padding: 12px 14px; }
        .patient-reservation { background: #fffdf8; border: 1px solid #c9f2d8; border-radius: 14px; display: grid; gap: 14px; grid-template-columns: repeat(2, minmax(0, 1fr)); margin-bottom: 18px; padding: 20px; }
        .patient-reservation label { display: grid; gap: 7px; font-size: 13px; font-weight: 900; }
        .patient-reservation input, .patient-reservation select, .patient-reservation textarea { background: #fff; border: 1px solid #baf0cc; border-radius: 12px; font: inherit; padding: 12px 13px; }
        .patient-reservation textarea { resize: vertical; }
        .patient-reservation-wide, .patient-reservation .patient-alert, .patient-reservation .patient-success, .patient-reservation button { grid-column: 1 / -1; }
        .patient-reservation button { align-items: center; background: #16a34a; border: 0; border-radius: 12px; color: #fff; cursor: pointer; display: inline-flex; font: inherit; font-weight: 900; gap: 8px; justify-content: center; padding: 14px 16px; }
        .patient-reservation button:disabled { cursor: wait; opacity: .7; }
        .patient-subtitle { font-size: 17px; margin: 8px 0 10px; }
        .patient-appointment-list { display: grid; gap: 10px; }
        .patient-appointment-card { align-items: center; background: #fffdf8; border: 1px solid #c9f2d8; border-radius: 12px; display: flex; gap: 12px; justify-content: space-between; padding: 14px 16px; }
        .patient-appointment-card strong { display: block; }
        .patient-appointment-card span { color: #5f7168; display: block; font-size: 13px; margin-top: 4px; }
        .patient-appointment-card em { background: #fef3c7; border-radius: 999px; color: #b45309; font-size: 12px; font-style: normal; font-weight: 900; padding: 7px 10px; }
        @media (max-width: 760px) {
          .patient-header { align-items: stretch; flex-direction: column; }
          .patient-summary { grid-template-columns: 1fr; }
          .patient-reservation { grid-template-columns: 1fr; }
          .patient-appointment-card { align-items: flex-start; flex-direction: column; }
        }
      `}</style>
    </>
  );
}
